import React, { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, ArrowUp, ArrowDown, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const inputCls = "w-full bg-transparent border border-ink/15 rounded-md py-2 px-3 text-ink text-sm focus:outline-none focus:border-trailride transition-colors";
const empty = { name: "", logo_url: "", website_url: "", order: 0 };

function PartnerEditor({ initial, onClose, onSaved }) {
  const editing = !!initial?.id;
  const [form, setForm] = useState(initial || empty);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { name: form.name, logo_url: form.logo_url || "", website_url: form.website_url || "", order: Number(form.order) || 0 };
    try {
      if (editing) await api.put(`/admin/partners/${initial.id}`, payload);
      else await api.post("/admin/partners", payload);
      toast.success(editing ? "Partner updated" : "Partner added");
      onSaved();
      onClose();
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-ink/40 flex items-start justify-center overflow-y-auto p-4" data-testid="partner-editor">
      <div className="bg-paper w-full max-w-lg rounded-lg my-8 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink/10">
          <h3 className="font-serif text-xl font-bold text-ink">{editing ? "Edit partner" : "New partner"}</h3>
          <button onClick={onClose} aria-label="Close" data-testid="partner-editor-close" className="text-ink/50 hover:text-ink"><X size={20} /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          <div>
            <label className="font-sans text-xs text-ink/60">Partner name</label>
            <input required value={form.name} onChange={set("name")} data-testid="partner-name" className={inputCls} />
          </div>
          <div>
            <label className="font-sans text-xs text-ink/60">Logo URL (optional — name shown if empty)</label>
            <input value={form.logo_url} onChange={set("logo_url")} placeholder="https://…" data-testid="partner-logo" className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-sans text-xs text-ink/60">Website URL</label>
              <input value={form.website_url} onChange={set("website_url")} placeholder="https://…" data-testid="partner-website" className={inputCls} />
            </div>
            <div>
              <label className="font-sans text-xs text-ink/60">Order</label>
              <input type="number" value={form.order} onChange={set("order")} data-testid="partner-order" className={inputCls} />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full border border-ink/20 font-sans text-sm text-ink hover:bg-ink/5">Cancel</button>
            <button type="submit" disabled={saving} data-testid="partner-save" className="px-6 py-2.5 rounded-full bg-trailride text-white font-sans text-sm font-medium hover:bg-trailride-dark disabled:opacity-60">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function PartnersTab() {
  const [partners, setPartners] = useState([]);
  const [editor, setEditor] = useState(null);
  const load = useCallback(() => api.get("/admin/partners").then((r) => setPartners(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const reorder = async (p, dir) => {
    try { await api.put(`/admin/partners/${p.id}`, { ...p, order: (p.order || 0) + dir }); load(); } catch {}
  };
  const remove = async (p) => {
    if (!window.confirm(`Remove ${p.name}?`)) return;
    try { await api.delete(`/admin/partners/${p.id}`); toast.success("Removed"); load(); } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-partners-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-2xl font-bold text-ink">Partners</h2>
          <p className="font-sans text-sm text-ink/55 mt-1">Add partner logos and website links (shown on the About page).</p>
        </div>
        <button onClick={() => setEditor({})} data-testid="admin-new-partner" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-trailride text-white font-sans text-sm font-medium hover:bg-trailride-dark">
          <Plus size={16} /> Add partner
        </button>
      </div>

      <div className="border border-ink/10 rounded-lg divide-y divide-ink/10">
        {partners.length === 0 && <p className="p-6 font-sans text-ink/50">No partners yet.</p>}
        {partners.map((p) => (
          <div key={p.id} className="p-4 flex items-center gap-4" data-testid={`admin-partner-row-${p.id}`}>
            <div className="w-16 h-10 bg-ink/5 rounded flex items-center justify-center overflow-hidden shrink-0">
              {p.logo_url ? <img src={p.logo_url} alt="" className="max-h-8 w-auto object-contain" /> : <span className="font-serif text-xs text-ink/50">{p.name.slice(0, 3)}</span>}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-sans font-semibold text-ink truncate">{p.name}</p>
              {p.website_url && <a href={p.website_url} target="_blank" rel="noreferrer" className="font-sans text-sm text-ink/50 hover:text-trailride inline-flex items-center gap-1 truncate">{p.website_url} <ExternalLink size={12} /></a>}
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => reorder(p, -1)} className="p-2 rounded-md hover:bg-ink/5 text-ink/60"><ArrowUp size={15} /></button>
              <button onClick={() => reorder(p, 1)} className="p-2 rounded-md hover:bg-ink/5 text-ink/60"><ArrowDown size={15} /></button>
              <button onClick={() => setEditor(p)} data-testid={`admin-edit-partner-${p.id}`} className="p-2 rounded-md hover:bg-ink/5 text-ink/60"><Pencil size={16} /></button>
              <button onClick={() => remove(p)} data-testid={`admin-delete-partner-${p.id}`} className="p-2 rounded-md hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      {editor && <PartnerEditor initial={editor.id ? editor : null} onClose={() => setEditor(null)} onSaved={load} />}
    </div>
  );
}
